import { useState } from 'react'
import { AlertTriangle, Download, Flag, Lock, RotateCcw } from 'lucide-react'
import { clsx } from 'clsx'
import { ConfirmDestructiveModal } from '@/shared/components/ui/ConfirmDestructiveModal'
import {
  useFinalizeGameweek,
  useLockGameweek,
  useResetGameweek,
  useSyncGameweekResults,
  type SurvivorAdminAction,
  type SurvivorAdminGameweek,
} from '../queries'
import styles from './GameweekOpsCard.module.css'

// The whole survivor operational loop, and nothing else:
//   Lock (fecha a janela, W.O. de quem não palpitou)
//     → Buscar resultados (1 request na API, grava os placares)
//       → Finalizar (resolve os palpites e aplica as eliminações)
// Reset is the way back out when a score was wrong — it un-finalizes the fantasy scoring
// AND the survivor round: this round's picks go back to PENDING, the eliminations and
// champions it produced are undone, and the fetched placares are dropped so nothing keeps
// reading the round as decided. Only the Lock's W.O. forfeits survive it. That puts the
// round back at step 2 of the loop: Buscar resultados, then Finalizar.
export function GameweekOpsCard({ gw }: { gw: SurvivorAdminGameweek }) {
  const [lastAction, setLastAction]   = useState<SurvivorAdminAction | null>(null)
  const [confirmReset, setConfirmReset] = useState(false)
  // Toasts fade; a refused action has to stay readable while the admin fixes it. Holds the
  // server's own reason — e.g. Finalizar's "results_required" when the placares are missing.
  const [actionError, setActionError] = useState<string | null>(null)

  const lock     = useLockGameweek()
  const results  = useSyncGameweekResults()
  const finalize = useFinalizeGameweek()
  const reset    = useResetGameweek()

  const anyPending = lock.isPending || results.isPending
                  || finalize.isPending || reset.isPending

  function onDone(action: SurvivorAdminAction) {
    setLastAction(action)
    setActionError(null)
  }

  const isPastRound = new Date(gw.lastMatchEndsAt) < new Date()

  const canLock     = gw.status === 'ACTIVE' || gw.status === 'UPCOMING'
  // Results can be pulled any time the round is under way or already over — including
  // before lock, so the admin can see what the feed has without committing anything.
  const canResults  = gw.status !== 'FINALIZED'
  const canFinalize = gw.status === 'LOCKED'
  const canReset    = gw.status === 'FINALIZED' || gw.status === 'LOCKED'

  // Only meaningful after a results pull in this session — the server is the authority.
  const lastResults = lastAction?.kind === 'results' ? lastAction.result : null
  const readyToFinalize = lastResults?.allMatchesFinished === true

  return (
    <>
      <div className={styles.card}>
        <header className={styles.header}>
          <div>
            <h2 className={styles.title}>
              Rodada {gw.number} — <span className={styles.phase}>{gw.phase}</span>
            </h2>
            <p className={styles.subtitle}>
              <span className={clsx(styles.statusBadge, styles[`status_${gw.status}`])}>
                {gw.status}
              </span>
              <span className={styles.subtitleSep}>·</span>
              Primeiro jogo: {formatDateTime(gw.firstMatchAt)}
            </p>
          </div>
        </header>

        {isPastRound && (gw.status === 'UPCOMING' || gw.status === 'ACTIVE') && (
          <p className={styles.notice}>
            Rodada já encerrada no calendário. Use <strong>Bloquear</strong> para registrar
            os W.O., depois <strong>Buscar resultados</strong> e <strong>Finalizar</strong>.
          </p>
        )}

        {actionError && (
          <p className={styles.errorNotice} role="alert">
            <AlertTriangle size={14} aria-hidden="true" />
            <span>{actionError}</span>
          </p>
        )}

        {readyToFinalize && gw.status === 'LOCKED' && (
          <p className={styles.readyNotice}>
            Todas as partidas têm resultado — a rodada pode ser finalizada.
          </p>
        )}

        <div className={styles.actions}>
          <ActionButton
            icon={<Lock size={14} />}
            label="Bloquear"
            hint="Fecha os palpites; quem não escolheu é eliminado por W.O."
            disabled={!canLock || anyPending}
            isPending={lock.isPending}
            onClick={() => lock.mutate(gw.id, {
              onSuccess: (result) => onDone({ kind: 'lock', result }),
              onError:   (err) => setActionError(err.message),
            })}
          />
          <ActionButton
            icon={<Download size={14} />}
            label="Buscar resultados"
            hint="1 requisição no provedor — grava os placares da rodada"
            disabled={!canResults || anyPending}
            isPending={results.isPending}
            onClick={() => results.mutate(gw.id, {
              onSuccess: (result) => onDone({ kind: 'results', result }),
              onError:   (err) => setActionError(err.message),
            })}
          />
          <ActionButton
            icon={<Flag size={14} />}
            label="Finalizar"
            hint="Resolve os palpites, elimina quem perdeu e abre a próxima rodada"
            disabled={!canFinalize || anyPending}
            isPending={finalize.isPending}
            onClick={() => finalize.mutate(gw.id, {
              onSuccess: (result) => onDone({ kind: 'finalize', result }),
              onError:   (err) => setActionError(err.message),
            })}
          />
          <ActionButton
            icon={<RotateCcw size={14} />}
            label="Resetar"
            hint="Apaga placares, palpites e eliminações da rodada e volta para bloqueada"
            disabled={!canReset || anyPending}
            isPending={reset.isPending}
            onClick={() => setConfirmReset(true)}
            variant="danger"
          />
        </div>

        {lastAction && <LastActionPanel action={lastAction} />}
      </div>

      {confirmReset && (
        <ConfirmDestructiveModal
          title="Resetar rodada?"
          body={
            'A rodada volta para BLOQUEADA para que possa ser finalizada de novo com os ' +
            'placares corrigidos.\n\n' +
            'Os palpites desta rodada voltam a ficar em aberto e as eliminações e os ' +
            'campeões que ela gerou são desfeitos — o Sobrevivente é recalculado do zero ' +
            'ao finalizar de novo.\n\n' +
            'Quem foi eliminado por W.O. no Bloquear continua eliminado.\n\n' +
            'Os placares desta rodada também são apagados: rode "Buscar resultados" de novo ' +
            'antes de finalizar.'
          }
          confirmText={`Resetar rodada ${gw.number}`}
          isPending={reset.isPending}
          onConfirm={() => reset.mutate(gw.id, {
            onSuccess: (result) => {
              onDone({ kind: 'reset', result })
              setConfirmReset(false)
            },
            onError: (err) => {
              setActionError(err.message)
              setConfirmReset(false)
            },
          })}
          onCancel={() => setConfirmReset(false)}
        />
      )}
    </>
  )
}

type ActionButtonProps = {
  icon:      React.ReactNode
  label:     string
  hint:      string
  disabled:  boolean
  isPending: boolean
  onClick:   () => void
  variant?:  'default' | 'danger'
}

function ActionButton({
  icon, label, hint, disabled, isPending, onClick, variant = 'default',
}: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(styles.actionBtn, variant === 'danger' && styles.actionBtnDanger)}
      title={hint}
    >
      <span className={styles.actionIcon}>
        {isPending ? <span className={styles.spinner} aria-hidden="true" /> : icon}
      </span>
      <span className={styles.actionText}>
        <span className={styles.actionLabel}>{label}</span>
        <span className={styles.actionHint}>{hint}</span>
      </span>
    </button>
  )
}

function LastActionPanel({ action }: { action: SurvivorAdminAction }) {
  return (
    <div className={styles.lastAction}>
      <div className={styles.lastActionHeader}>
        Última ação: <strong>{ACTION_LABEL[action.kind]}</strong>
      </div>
      <pre className={styles.lastActionBody}>
        {action.kind === 'lock'
          ? 'OK — palpites fechados.'
          : JSON.stringify(action.result, null, 2)}
      </pre>
    </div>
  )
}

const ACTION_LABEL: Record<SurvivorAdminAction['kind'], string> = {
  lock:     'Bloquear',
  results:  'Buscar resultados',
  finalize: 'Finalizar',
  reset:    'Resetar',
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day:    '2-digit',
    month:  '2-digit',
    year:   '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
  })
}
